import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useAuth0 } from '@auth0/auth0-react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { routeTree } from './routeTree.gen'
import { AuthLoading } from './components/auth/auth-loading'
import { setTokenGetter, setLoginRedirect } from './lib/api/client'
import { log } from './lib/logger'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
})

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient,
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function InnerApp() {
  const auth = useAuth0()

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      setTokenGetter(() => auth.getAccessTokenSilently())
      // When silent refresh fails OR API returns 401, apiClient calls this to
      // hand the user back to Auth0. Preserves the current URL so post-login
      // returnTo bounces them back to where they were.
      setLoginRedirect(() =>
        auth.loginWithRedirect({
          // Force re-prompt on the session-expired recovery path. Without this
          // Auth0 silently SSO's the user as whoever has the tenant-level
          // session cookie — e.g. if they logged into era-web (same tenant)
          // as a different user, agent-hub redirect would silently swap them
          // to that account. Verified 2026-05-01: patient3 era-web session
          // bled into agent-hub during recovery. `prompt: 'login'` forces a
          // fresh login form regardless of existing tenant session.
          authorizationParams: {
            prompt: 'login',
          },
          appState: {
            returnTo: window.location.pathname + window.location.search,
          },
        }),
      )
    }
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    auth.getAccessTokenSilently,
    auth.loginWithRedirect,
  ])

  // Sync the authenticated user onto the Sentry scope so errors AND logs are
  // filterable by user. Internal tool, so id + email + name are all included.
  useEffect(() => {
    if (auth.isLoading) return
    if (auth.isAuthenticated && auth.user?.sub) {
      Sentry.setUser({
        id: auth.user.sub,
        email: auth.user.email,
        username: auth.user.name,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.user])

  // Session-start log + route-navigation logs. The router subscription returns
  // its own unsubscribe function for cleanup.
  useEffect(() => {
    log.info('App session started', { path: window.location.pathname })
    return router.subscribe('onResolved', ({ toLocation }) => {
      log.info('Navigated to route', { path: toLocation.pathname })
    })
  }, [])

  if (auth.isLoading) {
    return <AuthLoading />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ auth }} />
    </QueryClientProvider>
  )
}
